import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatestWith, filter, Subscription, tap, withLatestFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { authHardLogin } from '../../../core/auth/auth.actions';
import {
	selectAuthError,
	selectAuthLoginState,
	selectSettledAuthLoginState
} from '../../../core/auth/auth.selectors';
import { saveClickLog } from '../../../core/clicks/click.actions';
import { ClickLog, ClickOperation } from '../../../core/clicks/click.models';
import { showSnackbar } from '../../../core/notifications/notification.actions';
import { NotificationService } from '../../../core/notifications/notification.service';
import { navigateToContext } from '../../../core/router/routes.actions';
import { ErrorMessage } from '../../../domain/error/error-message.model';
import { retrievePassword } from '../../../domain/user/user.actions';
import { selectPasswordRetrieved, selectUserIsActivated } from '../../../domain/user/user.selectors';
import { getPermission } from '../../util/util';
import { closeDialog } from '../dn-dialog.actions';
import { DottnetDialogComponent } from '../dn-dialog.component';
import {
  PayloadSignin,
  SigninFormComponent,
} from '../signin-dialog/signin-form/signin-form.component';
import {
  ContentPermission,
  PermissionVisibility,
  PermissionVisualizationType,
} from './permission.model';
import { LogService } from '../../../core/log/log.service';
import { AuthLoginState } from '../../../core/auth/auth.models';
import { selectSessionID } from '../../../core/session/session.selectors';
import { ParamMap, Params, Router, convertToParamMap } from '@angular/router';
import { PermissionDialogData } from './permission.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-permission-dialog',
  templateUrl: './permission-dialog.component.html',
  styleUrls: ['./permission-dialog.component.scss'],
  imports: [
    SigninFormComponent,
    MatDialogContent,
    MatDialogTitle,
  ],
})
export class PermissionDialogComponent
  extends DottnetDialogComponent<PermissionDialogComponent>
  implements OnInit, OnDestroy {
  authState: string;
  permissionBody: string = '';
  permissionType = signal<PermissionVisualizationType>(PermissionVisualizationType.PermissionAllowed);
  passwordRetrieved = toSignal(this.store.select(selectPasswordRetrieved), { initialValue: false });

  // Subs
  authStateSub: Subscription;
  abilitatoStateSub: Subscription;
  authErrorSub: Subscription;

  url: string = '';
  oldSessionID: number = 0;


  //  Reference to access the enum in the template
  readonly PermissionVisualizationType = PermissionVisualizationType;
  readonly ContentPermission = ContentPermission;
  readonly Visibility = PermissionVisibility;

  constructor(
    private ts: TranslateService,
    dialogRef: MatDialogRef<PermissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private permissionData: PermissionDialogData,
    public store: Store,
    private logService: LogService,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private notificationService: NotificationService
  ) {
    super(dialogRef, undefined, store, platformId);
  }

  ngOnInit() {
    if (this.permissionData.permissionType) {
      this.permissionType.set(this.permissionData.permissionType);
    }

    if (this.permissionData.permissionBody) {
      this.permissionBody = this.permissionData.permissionBody;
    }

    this.authErrorSub = this.store
      .select(selectAuthError)
      .pipe(
        filter((errorMessage: ErrorMessage) => !!errorMessage),
        tap((errorMessage: ErrorMessage) =>
          this.store.dispatch(
            showSnackbar({
              payload: this.notificationService.getErrorNotification(
                errorMessage.Code,
                this.ts.instant('dottnet.error.' + errorMessage.Code),
                'OK',
                0
              )
            })
          )
        )
      )
      .subscribe();

    // to ri-evaluate permissions we need to subscribe to isAbilitato changes
    // only when selectorAilitato emit we combine value with  latest value of authLoginState
    // we need to combine with emailConfirmationStatus value returned by selectUserIsActivated selector to filter the chain
    if (this.permissionData.selectorAbilitato) {
      // NB: assign to abilitatoStateSub — the authStateSub handle is reused by the
      // selectAuthLoginState subscription below; assigning here too would orphan
      // this subscription (it would never be unsubscribed in ngOnDestroy).
      this.abilitatoStateSub = this.permissionData.selectorAbilitato
        .pipe(
          // withLatestFrom
          // Creates a combined stream from source and other provided streams.
          // When source emits a value — resulting Observable will emit a combined value of source emission and all latest values on provided streams.
          withLatestFrom(
            // new-login (1.7): stato assestato per la proiezione del permesso
            this.store.select(selectSettledAuthLoginState),
            this.store
              .select(selectUserIsActivated)
              .pipe(filter((isActivated) => isActivated !== undefined))
          )
        )
        .subscribe(([isAbilitato, authLoginState, isActivated]) => {
          if (!this.permissionData.permissionType) {
            const currentPermissionType = getPermission(
              this.permissionData.templateVisibility,
              authLoginState,
              isAbilitato,
              isActivated
            );

            // PermissionAllowed and UserNotActivated both advance the caller's
            // nextAction. Activation is enforced by the SiteAccess feature
            // (mail-confirm is already open via `openGatesOnUserLoad$` when the
            // user isn't activated); the consumer waits on `siteAccessCleared`
            // before rendering. Dispatching nextAction here flips the
            // content-permission state forward without waiting for activation.
            if (
              currentPermissionType === PermissionVisualizationType.PermissionAllowed ||
              currentPermissionType === PermissionVisualizationType.UserNotActivated
            ) {
              if (this.permissionData.nextAction) {
                this.store.dispatch(this.permissionData.nextAction);
              }
            } else {
              this.permissionType.set(currentPermissionType);
            }

            // this.changeDetector.markForCheck();
          } else {
            if (
              this.permissionData.permissionType === PermissionVisualizationType.PermissionAllowed
            ) {
              if (this.permissionData.nextAction) {
                this.store.dispatch(this.permissionData.nextAction);
              }
            }
          }
        });
    }
    this.authStateSub = this.store
      .select(selectAuthLoginState)
      .pipe(
        filter((authLoginState) => authLoginState === AuthLoginState.LOGGEDHARD),
        combineLatestWith(this.store.select(selectSessionID))
      )
      .subscribe(([_, oldSessionID]) => {
        this.logService.info('Creating session after login. Old session: ', oldSessionID);
        this.url = this.router.url;
        this.oldSessionID = oldSessionID;
        if (this.permissionType() === PermissionVisualizationType.Signin || this.permissionType() === PermissionVisualizationType.UserSessionExpired) {
          // ricreaiamo una nuova sessione dopo la login, quando dobbiamo di fatto loggarci per veder il contenuto o quando la sessione è scaduta e dobbiamo rifare il login
          // prima della closeDialog(), ora viene dispatchata la nextAction (che nel caso dei dynamic container sarà checkExternalConsents se c'è un clientId,
          // oppure containerOperationOk() direttamente).
          // Così il flusso prosegue correttamente e containerOperation passa da LOADING a OK, sbloccando il rendering del dynamic container.
          if (this.permissionData.nextAction) {
            this.store.dispatch(this.permissionData.nextAction);
          }
          super.hardedLoginOk();
        }
      });

    // this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.abilitatoStateSub) this.abilitatoStateSub.unsubscribe();
    if (this.authStateSub) this.authStateSub.unsubscribe();
    if (this.authErrorSub) this.authErrorSub.unsubscribe();
  }

  closeDialog() {
    // super.onExit();
    this.store.dispatch(closeDialog());
  }

  getPermissionTitle(): string {
    let permissionTitle;

    if (this.permissionData.permissionTitle) {
      return this.permissionData.permissionTitle;
    }

    switch (this.permissionType()) {
      case PermissionVisualizationType.Signin:
        permissionTitle = `${this.ts.instant('dottnet.permessi.login')}`;
        break;
      case PermissionVisualizationType.Unauthorized:
        permissionTitle = `${this.ts.instant('dottnet.permessi.unauthorizedTitle')}`;
        break;
      case PermissionVisualizationType.NotFound:
        permissionTitle = `${this.ts.instant('dottnet.permessi.notFoundTitle')}`;
        break;
      case PermissionVisualizationType.UserSessionExpired:
        permissionTitle = `${this.ts.instant('dottnet.permessi.sessionExpiredTitle')}`;
        break;
      case PermissionVisualizationType.PermissionAllowed:
        break;
      default:
        permissionTitle = `${this.ts.instant('dottnet.permessi.attenzione')}`;
        break;
    }

    return permissionTitle;
  }

  getPermissionBody(): string {
    let permissionBody = '';

    if (this.permissionData.permissionBody) {
      return this.permissionData.permissionBody;
    }

    switch (this.permissionType()) {
      case PermissionVisualizationType.Unauthorized:
        permissionBody = `${this.ts.instant('dottnet.permessi.unauthorized')}`;
        break;
      case PermissionVisualizationType.NotFound:
        permissionBody = `${this.ts.instant('dottnet.permessi.notFound')}`;
        break;
      case undefined:
        permissionBody = '';
        break;
      case PermissionVisualizationType.PermissionAllowed:
        break;
      default:
        permissionBody = `${this.ts.instant('dottnet.shared.assistenza')}`;
        break;
    }

    return permissionBody;
  }

  onRetrievePassword(email: string) {
    const clickLog: ClickLog = <ClickLog>{};
    clickLog.clickLogOperationID = ClickOperation.RecuperaPassword;
    clickLog.content = JSON.stringify({ email });
    clickLog.message = 'Recupera password';
    clickLog.method = 'CLICK';
    clickLog.outcome = true;
    this.store.dispatch(saveClickLog({ clickLog }));
    this.store.dispatch(retrievePassword({ email }));
  }

  onPost(payloadSignin: PayloadSignin) {
    let referrer: string = '';
    if (isPlatformBrowser(this.platformId)) {
      referrer = this.document.referrer;
    }
    const par: Params = <Params>{};
    const params: ParamMap = convertToParamMap(par);
    const oldSessionID = this.oldSessionID;
    const url = this.url;
    this.store.dispatch(authHardLogin({ payloadSignin, params, oldSessionID, url, referrer }));
  }

  goToContext() {
    super.onExit();
    // if permissionType is not of simple notification type, go to context to disable access to page
    if (this.permissionType() !== PermissionVisualizationType.NotifyPermission) {
      this.store.dispatch(navigateToContext());
    }
  }
}
